(() => {
  const ready = (fn) => document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn, { once: true })
    : fn();

  const slugify = (text) => (text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || `section-${Math.random().toString(36).slice(2, 8)}`;

  const uniqueId = (base, used) => {
    let id = base;
    let n = 2;
    while (used.has(id) || document.getElementById(id)) id = `${base}-${n++}`;
    used.add(id);
    return id;
  };

  const policyLinks = [
    ['Return & Refund', '/policies/refund-policy'],
    ['Privacy', '/policies/privacy-policy'],
    ['Terms of Service', '/policies/terms-of-service'],
    ['Shipping', '/policies/shipping-policy'],
    ['Contact Information', '/policies/contact-information'],
    ['Legal Notice', '/policies/legal-notice']
  ];

  const normalisePath = (value) => (value || '/').replace(/\/$/, '') || '/';

  const buildPolicyNav = (container) => {
    if (!container || container.querySelector('[data-coasta-policy-nav]')) return;
    const current = normalisePath(window.location.pathname);
    const nav = document.createElement('nav');
    nav.className = 'coasta-policy-nav';
    nav.setAttribute('aria-label', 'Policy pages');
    nav.setAttribute('data-coasta-policy-nav', '');

    const intro = document.createElement('div');
    intro.className = 'coasta-policy-nav__intro';
    intro.innerHTML = '<span class="coasta-policy-nav__eyebrow">COASTA POLICY CENTER</span><span class="coasta-policy-nav__hint">Policies, terms and customer information</span>';

    const list = document.createElement('div');
    list.className = 'coasta-policy-nav__list';

    policyLinks.forEach(([label, href]) => {
      const a = document.createElement('a');
      a.className = 'coasta-policy-nav__link';
      a.href = href;
      a.textContent = label;
      if (current === normalisePath(href)) {
        a.classList.add('is-active');
        a.setAttribute('aria-current', 'page');
      }
      list.append(a);
    });

    nav.append(intro, list);
    container.prepend(nav);
  };

  const buildToc = (root, content) => {
    if (!root || !content || root.dataset.coastaSupportReady === 'true') return;
    root.dataset.coastaSupportReady = 'true';

    const headings = [...content.querySelectorAll('h2, h3')]
      .filter((heading) => heading.textContent.trim().length > 0);

    if (headings.length < 2) {
      root.classList.add('coasta-support-no-toc');
      return;
    }

    const used = new Set();
    headings.forEach((heading) => {
      if (!heading.id) heading.id = uniqueId(slugify(heading.textContent), used);
    });

    const toc = document.createElement('aside');
    toc.className = 'coasta-support-toc';
    toc.setAttribute('aria-label', 'On this page');
    toc.innerHTML = '<p class="coasta-support-toc__label">On this page</p><ul class="coasta-support-toc__list"></ul>';
    const list = toc.querySelector('.coasta-support-toc__list');

    headings.forEach((heading) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.className = `coasta-support-toc__link${heading.tagName === 'H3' ? ' coasta-support-toc__link--h3' : ''}`;
      a.href = `#${heading.id}`;
      a.textContent = heading.textContent.trim();
      a.addEventListener('click', (event) => {
        event.preventDefault();
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', `#${heading.id}`);
      });
      li.append(a);
      list.append(li);
    });

    // Do not move or clone Shopify's policy body. A wrapper is inserted and
    // the existing body is placed in it once, preserving Shopify-rendered copy.
    const layout = document.createElement('div');
    layout.className = 'coasta-support-document__layout';
    content.parentNode.insertBefore(layout, content);
    layout.append(toc);
    layout.append(content);

    if ('IntersectionObserver' in window) {
      const links = new Map([...list.querySelectorAll('a')].map((a) => [a.getAttribute('href').slice(1), a]));
      const observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (!visible) return;
        links.forEach((link) => link.classList.remove('is-active'));
        links.get(visible.target.id)?.classList.add('is-active');
      }, { rootMargin: '-18% 0px -68% 0px', threshold: 0 });
      headings.forEach((heading) => observer.observe(heading));
    }
  };

  const addHelpCard = (container) => {
    if (!container || container.querySelector('[data-coasta-policy-help]')) return;
    const card = document.createElement('div');
    card.className = 'coasta-policy-help';
    card.setAttribute('data-coasta-policy-help', '');
    card.innerHTML = `
      <div>
        <span class="coasta-policy-help__eyebrow">NEED HELP?</span>
        <strong>Questions about a policy?</strong>
        <p>Our support team can help with orders, shipping, returns and warranty questions.</p>
      </div>
      <a class="coasta-policy-help__button" href="/pages/contact-us">CONTACT US <span aria-hidden="true">→</span></a>`;
    container.append(card);
  };

  const enhancePolicy = () => {
    const policy = document.querySelector('.shopify-policy__container');
    if (!policy) return;

    policy.classList.add('coasta-support-enhanced');
    buildPolicyNav(policy);

    const title = policy.querySelector('.shopify-policy__title');
    if (title) title.classList.add('coasta-policy-title');

    const body = policy.querySelector('.shopify-policy__body');
    const rte = body?.querySelector('.rte') || body;

    if (body) {
      const text = (body.textContent || '').replace(/\s+/g, ' ').trim();
      body.classList.toggle('is-empty', text.length === 0);
      if (text.length > 0) buildToc(policy, body);
    }

    addHelpCard(policy);
  };

  ready(() => {
    document.querySelectorAll('[data-coasta-support-document]').forEach((root) => {
      buildToc(root, root.querySelector('[data-coasta-support-content]'));
    });
    enhancePolicy();
  });
})();
